// src/utils/dynamicSceneGenerator.js
// Dynamic scene generation using CESDK and background library

import { selectOptimalBackgrounds } from './backgroundLibrary';

/**
 * Dynamic Scene Generator for creating AI-powered sports designs
 * Uses CESDK to composite: analyzed image + optimal background + adaptive layout
 */

// Pre-defined layout templates for different content types
export const LAYOUT_TEMPLATES = {
  portrait_player_card: {
    name: 'Player Card Portrait',
    description: 'Vertical player card with stats area',
    dimensions: { width: 1080, height: 1350 },
    imageArea: { x: 100, y: 150, width: 880, height: 800 },
    textAreas: [
      { x: 100, y: 980, width: 880, height: 80, type: 'player_name', fontSize: 48 },
      { x: 100, y: 1080, width: 400, height: 60, type: 'position', fontSize: 32 },
      { x: 580, y: 1080, width: 400, height: 60, type: 'number', fontSize: 32 },
      { x: 100, y: 1160, width: 880, height: 120, type: 'stats', fontSize: 24 }
    ],
    overlays: ['team_logo', 'sport_elements']
  },
  
  landscape_action_shot: {
    name: 'Action Shot Landscape',
    description: 'Horizontal action layout with dynamic text',
    dimensions: { width: 1350, height: 1080 },
    imageArea: { x: 50, y: 50, width: 800, height: 980 },
    textAreas: [
      { x: 900, y: 150, width: 400, height: 100, type: 'player_name', fontSize: 44 },
      { x: 900, y: 280, width: 400, height: 200, type: 'action_text', fontSize: 28 },
      { x: 900, y: 520, width: 400, height: 120, type: 'game_info', fontSize: 22 }
    ],
    overlays: ['action_effects', 'dynamic_elements']
  },
  
  team_collage: {
    name: 'Team Collage',
    description: 'Multi-player team layout',
    dimensions: { width: 1350, height: 1080 },
    imageArea: { x: 50, y: 50, width: 1250, height: 700 },
    textAreas: [
      { x: 50, y: 780, width: 1250, height: 80, type: 'team_name', fontSize: 52 },
      { x: 50, y: 880, width: 600, height: 60, type: 'season', fontSize: 32 },
      { x: 700, y: 880, width: 600, height: 60, type: 'league', fontSize: 32 }
    ],
    overlays: ['team_branding', 'championship_elements']
  },
  
  stats_showcase: {
    name: 'Stats Showcase',
    description: 'Player photo with prominent statistics',
    dimensions: { width: 1080, height: 1080 },
    imageArea: { x: 50, y: 50, width: 500, height: 600 },
    textAreas: [
      { x: 580, y: 100, width: 450, height: 80, type: 'player_name', fontSize: 36 },
      { x: 580, y: 200, width: 450, height: 400, type: 'stats_grid', fontSize: 28 },
      { x: 50, y: 680, width: 980, height: 120, type: 'achievement', fontSize: 24 }
    ],
    overlays: ['stats_graphics', 'achievement_badges']
  },

  game_day_announcement: {
    name: 'Game Day Announcement',
    description: 'Bold "GAME DAY" style layout like your baseball example',
    dimensions: { width: 1080, height: 1920 },
    imageArea: { x: 200, y: 800, width: 680, height: 900 },
    textAreas: [
      { x: 50, y: 200, width: 500, height: 400, type: 'game_day_text', fontSize: 120, fontWeight: 'black', color: '#FF6B35', textAlign: 'left' },
      { x: 100, y: 1750, width: 880, height: 100, type: 'opponent_team', fontSize: 64, fontWeight: 'bold', color: '#000000', textAlign: 'left' },
      { x: 200, y: 100, width: 680, height: 80, type: 'player_name', fontSize: 48, color: '#FFFFFF', textAlign: 'center' }
    ],
    overlays: ['bold_graphics', 'team_branding'],
    specialEffects: ['dramatic_lighting']
  },

  color_dust_portrait: {
    name: 'Color Dust Portrait',
    description: 'Artistic color dust effect like your cheerleader example',
    dimensions: { width: 1080, height: 1920 },
    imageArea: { x: 190, y: 300, width: 700, height: 1100 },
    textAreas: [
      { x: 100, y: 120, width: 880, height: 120, type: 'organization_name', fontSize: 72, fontWeight: 'bold', color: '#000000', textAlign: 'center' },
      { x: 100, y: 1500, width: 880, height: 120, type: 'player_name', fontSize: 64, fontWeight: 'bold', color: '#000000', textAlign: 'center' },
      { x: 100, y: 1650, width: 880, height: 80, type: 'team_level', fontSize: 42, color: '#F1C40F', textAlign: 'center' }
    ],
    overlays: ['color_dust_effects', 'artistic_elements'],
    specialEffects: ['color_dust', 'powder_explosion']
  },

  media_day_portrait: {
    name: 'Media Day Portrait',
    description: 'Professional media day layout for team photos and individual portraits',
    dimensions: { width: 1080, height: 1350 },
    imageArea: { x: 90, y: 200, width: 900, height: 900 },
    textAreas: [
      { x: 100, y: 50, width: 880, height: 100, type: 'organization_name', fontSize: 56, fontWeight: 'bold', color: '#000000', textAlign: 'center' },
      { x: 100, y: 1150, width: 880, height: 100, type: 'player_name', fontSize: 64, fontWeight: 'bold', color: '#FFFFFF', textAlign: 'center' },
      { x: 100, y: 1250, width: 400, height: 80, type: 'position', fontSize: 36, color: '#FFFFFF', textAlign: 'left' },
      { x: 580, y: 1250, width: 400, height: 80, type: 'number', fontSize: 36, color: '#FFFFFF', textAlign: 'right' }
    ],
    overlays: ['professional_frame', 'team_branding'],
    specialEffects: ['clean_professional']
  }
};

// Sport-specific overlay elements
export const SPORT_OVERLAYS = {
  baseball: {
    elements: ['baseball_diamond', 'baseball_stitches', 'home_plate', 'bat_silhouette'],
    colors: ['#2E8B57', '#8B4513', '#FFD700', '#FFFFFF'], // Green, brown, gold, white
    textures: ['grass_texture', 'dirt_texture', 'leather_texture']
  },
  basketball: {
    elements: ['basketball_lines', 'hoop_silhouette', 'court_markings', 'basketball_texture'],
    colors: ['#FF8C00', '#000000', '#FFFFFF', '#8B0000'], // Orange, black, white, red
    textures: ['court_wood', 'basketball_leather', 'net_texture']
  },
  football: {
    elements: ['football_laces', 'yard_lines', 'goal_posts', 'helmet_silhouette'],
    colors: ['#006400', '#FFFFFF', '#8B4513', '#FFD700'], // Green, white, brown, gold
    textures: ['grass_texture', 'leather_texture', 'turf_texture']
  },
  soccer: {
    elements: ['soccer_ball', 'goal_net', 'field_lines', 'cleat_marks'],
    colors: ['#228B22', '#FFFFFF', '#000000', '#FFD700'], // Green, white, black, gold
    textures: ['grass_texture', 'net_texture', 'ball_texture']
  }
};

/**
 * Generates a dynamic scene based on content analysis and event context
 * @param {object} config - Generation configuration
 * @returns {Promise<object>} - Generated scene data and CESDK instructions
 */
export const generateDynamicScene = async (config) => {
  const {
    uploadedImageURL,
    contentAnalysis,
    eventTags = {},
    preferredLayout = null,
    customization = {}
  } = config;

  console.log('Starting dynamic scene generation:', {
    hasImage: !!uploadedImageURL,
    hasAnalysis: !!contentAnalysis,
    eventTags
  });

  try {
    // Step 1: Select optimal background
    const recommendedBackgrounds = await selectOptimalBackgrounds(
      contentAnalysis, 
      eventTags, 
      { maxResults: 3 }
    );

    // Step 2: Determine optimal layout
    const selectedLayout = determineOptimalLayout(contentAnalysis, eventTags, preferredLayout);

    // Step 3: Generate sport-specific overlays
    const overlayElements = generateSportOverlays(contentAnalysis, eventTags);

    // Step 4: Create adaptive text areas
    const adaptiveTextAreas = createAdaptiveTextAreas(selectedLayout, contentAnalysis, eventTags);

    // Step 5: Generate CESDK scene configuration
    const sceneConfig = await createCESDKSceneConfig({
      layout: selectedLayout,
      background: recommendedBackgrounds[0],
      userImage: uploadedImageURL,
      textAreas: adaptiveTextAreas,
      overlays: overlayElements,
      contentAnalysis,
      eventTags,
      customization
    });

    return {
      success: true,
      sceneConfig,
      metadata: {
        selectedLayout: selectedLayout.name,
        selectedBackground: recommendedBackgrounds[0],
        alternativeBackgrounds: recommendedBackgrounds.slice(1),
        adaptations: getLayoutAdaptations(contentAnalysis),
        generationReason: `Generated for ${contentAnalysis?.sport?.primary || 'sports'} content with ${contentAnalysis?.characteristics?.peopleCount || 1} person(s)`
      }
    };

  } catch (error) {
    console.error('Error generating dynamic scene:', error);
    return {
      success: false,
      error: error.message,
      fallback: await generateFallbackScene(uploadedImageURL, eventTags)
    };
  }
};

/**
 * Determines the best layout template based on content analysis
 */
const determineOptimalLayout = (contentAnalysis, eventTags, preferredLayout) => {
  if (preferredLayout && LAYOUT_TEMPLATES[preferredLayout]) {
    return LAYOUT_TEMPLATES[preferredLayout];
  }

  const characteristics = contentAnalysis?.characteristics || {};
  const peopleCount = characteristics.peopleCount || 1;
  const isPortrait = characteristics.isPortrait;
  const isLandscape = characteristics.isLandscape;
  const sport = eventTags.sport || contentAnalysis?.sport?.primary || 'baseball';

  // Game Day announcements - high energy events
  if (eventTags.event_type === 'championship' || 
      eventTags.event_type === 'tournament' ||
      eventTags.event_type === 'playoff' ||
      eventTags.mood === 'triumphant' ||
      eventTags.focus === 'game_announcement') {
    return LAYOUT_TEMPLATES.game_day_announcement;
  }

  // Media Day events - professional, clean layouts
  if (eventTags.event_type === 'media_day' || 
      eventTags.event_type === 'team_photo' ||
      eventTags.event_type === 'photo_day' ||
      eventTags.focus === 'professional' ||
      eventTags.mood === 'professional') {
    return LAYOUT_TEMPLATES.media_day_portrait;
  }

  // Color dust portrait for cheerleading or artistic sports
  if (sport === 'cheerleading' || 
      sport === 'dance' || 
      eventTags.design_style === 'artistic' ||
      eventTags.background_preference === 'colorful' ||
      eventTags.mood === 'celebratory') {
    return LAYOUT_TEMPLATES.color_dust_portrait;
  }

  // Multiple people → Team layout
  if (peopleCount > 1) {
    return LAYOUT_TEMPLATES.team_collage;
  }

  // Single person portrait → Player card (or color dust for artistic)
  if (peopleCount === 1 && isPortrait) {
    // Check if this should be artistic style
    if (eventTags.design_intensity === 'bold' || eventTags.mood === 'dynamic') {
      return LAYOUT_TEMPLATES.color_dust_portrait;
    }
    return LAYOUT_TEMPLATES.portrait_player_card;
  }

  // Single person landscape → Action shot (or game day for big events)
  if (peopleCount === 1 && isLandscape) {
    // Check if this is a major event
    if (eventTags.event_type === 'championship' || eventTags.mood === 'triumphant') {
      return LAYOUT_TEMPLATES.game_day_announcement;
    }
    return LAYOUT_TEMPLATES.landscape_action_shot;
  }

  // Stats or achievement focus
  if (eventTags.focusType === 'stats' || eventTags.includeStats) {
    return LAYOUT_TEMPLATES.stats_showcase;
  }

  // Default based on sport
  if (sport === 'baseball' || sport === 'softball') {
    return LAYOUT_TEMPLATES.game_day_announcement; // Baseball often uses bold announcements
  } else if (sport === 'cheerleading') {
    return LAYOUT_TEMPLATES.color_dust_portrait;
  }

  // Final fallback to portrait player card
  return LAYOUT_TEMPLATES.portrait_player_card;
};

/**
 * Generates sport-specific overlay elements
 */
const generateSportOverlays = (contentAnalysis, eventTags) => {
  const sport = eventTags.sport || contentAnalysis?.sport?.primary || 'baseball';
  const sportData = SPORT_OVERLAYS[sport.toLowerCase()] || SPORT_OVERLAYS.baseball;

  return {
    sport,
    elements: sportData.elements,
    primaryColors: sportData.colors,
    textures: sportData.textures,
    intensity: eventTags.designIntensity || 'medium' // subtle, medium, bold
  };
};

/**
 * Creates adaptive text areas based on content and context
 */
const createAdaptiveTextAreas = (layout, contentAnalysis, eventTags) => {
  const baseTextAreas = [...layout.textAreas];
  
  // Adapt text content based on event tags
  const adaptedAreas = baseTextAreas.map(area => {
    const adaptedArea = { ...area };
    
    // Set placeholder content based on area type and event context
    switch (area.type) {
      case 'player_name':
        adaptedArea.placeholder = eventTags.player_name || eventTags.playerName || 'PLAYER NAME';
        adaptedArea.defaultText = eventTags.player_name || eventTags.playerName || '';
        break;
        
      case 'team_name':
        adaptedArea.placeholder = eventTags.team_name || eventTags.teamName || 'TEAM NAME';
        adaptedArea.defaultText = eventTags.team_name || eventTags.teamName || '';
        break;
        
      case 'position':
        adaptedArea.placeholder = eventTags.position || 'Position';
        adaptedArea.defaultText = eventTags.position || '';
        break;
        
      case 'number':
        adaptedArea.placeholder = eventTags.player_number || eventTags.playerNumber || '#00';
        adaptedArea.defaultText = eventTags.player_number || eventTags.playerNumber || '';
        break;
        
      case 'season':
        adaptedArea.placeholder = eventTags.season || '2024 Season';
        adaptedArea.defaultText = eventTags.season || '';
        break;
        
      case 'stats':
        adaptedArea.placeholder = generateStatsPlaceholder(eventTags);
        adaptedArea.defaultText = '';
        break;

      case 'game_day_text':
        adaptedArea.placeholder = "IT'S GAME DAY";
        adaptedArea.defaultText = "IT'S GAME DAY";
        break;

      case 'opponent_team':
        adaptedArea.placeholder = eventTags.opponent || eventTags.against || 'OPPONENT';
        adaptedArea.defaultText = eventTags.opponent || eventTags.against || '';
        break;

      case 'organization_name':
        adaptedArea.placeholder = eventTags.organization || eventTags.league || 'THE ORGANIZATION';
        adaptedArea.defaultText = eventTags.organization || eventTags.league || '';
        break;

      case 'team_level':
        adaptedArea.placeholder = `${eventTags.level || 'TEAM'} - ${eventTags.season || '2024'}`;
        adaptedArea.defaultText = `${eventTags.level || 'TEAM'} - ${eventTags.season || '2024'}`;
        break;
        
      default:
        adaptedArea.placeholder = `Add ${area.type.replace('_', ' ')}`;
    }
    
    // Adjust font size based on content analysis
    if (contentAnalysis?.characteristics?.isPortrait) {
      adaptedArea.fontSize = Math.round(area.fontSize * 0.9); // Slightly smaller for portrait
    }
    
    return adaptedArea;
  });

  return adaptedAreas;
};

/**
 * Creates CESDK scene configuration
 */
const createCESDKSceneConfig = async (config) => {
  const { layout, background, userImage, textAreas, overlays, contentAnalysis, eventTags, customization } = config;

  // Calculate optimal image positioning and effects
  const imageEffects = calculateImageEffects(contentAnalysis, eventTags);
  
  return {
    version: '1.0',
    type: 'cesdk_scene',
    dimensions: layout.dimensions,
    
    // Background layer
    background: {
      type: 'image',
      source: background.url,
      fit: 'cover',
      effects: ['subtle_blur', 'color_enhance'],
      opacity: eventTags.backgroundOpacity || 0.85
    },
    
    // User image layer with smart positioning
    userImage: {
      type: 'image',
      source: userImage,
      area: layout.imageArea,
      fit: 'cover',
      effects: imageEffects,
      backgroundRemoval: eventTags.removeBackground !== false, // Default true
      borderRadius: eventTags.imageStyle === 'circular' ? '50%' : '8px',
      shadow: {
        enabled: true,
        blur: 15,
        opacity: 0.3,
        offset: { x: 0, y: 5 }
      }
    },
    
    // Dynamic text layers
    textLayers: textAreas.map((area, index) => ({
      id: `text_${area.type}_${index}`,
      type: 'text',
      content: area.defaultText || area.placeholder,
      area: {
        x: area.x,
        y: area.y,
        width: area.width,
        height: area.height
      },
      style: {
        fontSize: area.fontSize,
        fontFamily: eventTags.fontFamily || 'Arial Black',
        fontWeight: 'bold',
        color: eventTags.textColor || '#FFFFFF',
        textAlign: area.textAlign || 'center',
        textShadow: {
          blur: 4,
          opacity: 0.8,
          offset: { x: 2, y: 2 },
          color: '#000000'
        }
      },
      placeholder: area.placeholder,
      editable: true
    })),
    
    // Sport-specific overlay elements
    overlayLayers: generateOverlayLayers(overlays, layout, eventTags),
    
    // Metadata for customization
    metadata: {
      sport: overlays.sport,
      layoutType: layout.name,
      generatedAt: Date.now(),
      eventContext: eventTags,
      contentAnalysis: {
        peopleCount: contentAnalysis?.characteristics?.peopleCount,
        orientation: contentAnalysis?.characteristics?.isPortrait ? 'portrait' : 'landscape',
        brightness: contentAnalysis?.characteristics?.brightness
      },
      customization: customization
    }
  };
};

/**
 * Calculates optimal image effects based on content analysis
 */
const calculateImageEffects = (contentAnalysis, eventTags) => {
  const effects = [];
  const characteristics = contentAnalysis?.characteristics || {};
  
  // Auto-enhance based on brightness
  if (characteristics.brightness === 'dark') {
    effects.push({ type: 'brightness', value: 1.2 });
    effects.push({ type: 'contrast', value: 1.1 });
  } else if (characteristics.brightness === 'bright') {
    effects.push({ type: 'brightness', value: 0.95 });
  }
  
  // Color enhancement for sports
  effects.push({ type: 'saturation', value: 1.1 });
  effects.push({ type: 'clarity', value: 1.05 });
  
  // Sport-specific filters
  const sport = eventTags.sport || contentAnalysis?.sport?.primary || 'baseball';
  if (sport === 'baseball') {
    effects.push({ type: 'warmth', value: 1.1 }); // Warmer tone for baseball
  } else if (sport === 'basketball') {
    effects.push({ type: 'vibrance', value: 1.15 }); // More vibrant for basketball
  }
  
  return effects;
};

/**
 * Generates sport-specific overlay layers
 */
const generateOverlayLayers = (overlays, layout, eventTags) => {
  const layers = [];
  const intensity = overlays.intensity || 'medium';
  
  // Sport elements overlay
  if (intensity !== 'subtle') {
    layers.push({
      type: 'vector_elements',
      elements: overlays.elements.slice(0, intensity === 'bold' ? 4 : 2),
      opacity: intensity === 'bold' ? 0.3 : 0.15,
      blendMode: 'soft_light',
      positioning: 'corners' // Place elements in corners to avoid interfering with main content
    });
  }
  
  // Team colors accent
  if (eventTags.teamColors && eventTags.teamColors.length > 0) {
    layers.push({
      type: 'color_accent',
      colors: eventTags.teamColors,
      style: 'gradient_border',
      width: 8,
      opacity: 0.7
    });
  }
  
  // Achievement badges
  if (eventTags.achievements && eventTags.achievements.length > 0) {
    layers.push({
      type: 'achievement_badges',
      badges: eventTags.achievements,
      position: 'top_right',
      size: 'small'
    });
  }
  
  return layers;
};

/**
 * Generates stats placeholder text
 */
const generateStatsPlaceholder = (eventTags) => {
  const sport = eventTags.sport || 'baseball';
  
  const statTemplates = {
    baseball: 'AVG: .325 • HR: 12 • RBI: 45',
    basketball: 'PPG: 18.5 • RPG: 7.2 • APG: 4.1',
    football: 'YDS: 1,250 • TD: 8 • AVG: 5.2',
    soccer: 'Goals: 15 • Assists: 8 • Saves: 42'
  };
  
  return statTemplates[sport.toLowerCase()] || 'Season Stats';
};

/**
 * Gets layout adaptations made based on content analysis
 */
const getLayoutAdaptations = (contentAnalysis) => {
  const adaptations = [];
  const characteristics = contentAnalysis?.characteristics || {};
  
  if (characteristics.peopleCount > 1) {
    adaptations.push('Selected team layout for multiple people');
  }
  
  if (characteristics.isPortrait) {
    adaptations.push('Optimized for portrait orientation');
  }
  
  if (characteristics.brightness === 'dark') {
    adaptations.push('Added brightness enhancement for dark images');
  }
  
  return adaptations;
};

/**
 * Generates fallback scene when main generation fails
 */
const generateFallbackScene = async (uploadedImageURL, eventTags) => {
  return {
    type: 'simple_overlay',
    userImage: uploadedImageURL,
    layout: LAYOUT_TEMPLATES.portrait_player_card,
    background: 'default_sports_background',
    message: 'Using simplified layout due to generation error'
  };
};

/**
 * Validates and processes event tags for scene generation
 * @param {object} eventTags - Raw event tags from your system
 * @returns {object} - Processed tags for scene generation
 */
export const processEventTags = (eventTags) => {
  return {
    // Core identification
    sport: eventTags.sport || eventTags.sport_type || 'baseball',
    playerName: eventTags.player_name || eventTags.name,
    teamName: eventTags.team_name || eventTags.team,
    playerNumber: eventTags.number || eventTags.jersey_number,
    position: eventTags.position,
    
    // Event context
    season: eventTags.season || eventTags.year,
    league: eventTags.league || eventTags.division,
    eventType: eventTags.event_type || eventTags.type,
    
    // Visual preferences
    teamColors: eventTags.team_colors || eventTags.colors,
    fontFamily: eventTags.font_family,
    textColor: eventTags.text_color,
    designStyle: eventTags.style || 'modern',
    
    // Content flags
    includeStats: eventTags.include_stats || eventTags.show_stats,
    removeBackground: eventTags.remove_background !== false,
    backgroundOpacity: eventTags.background_opacity || 0.85,
    
    // Mood and tone
    mood: eventTags.mood || 'energetic',
    level: eventTags.level || eventTags.competitive_level || 'youth',
    timeOfDay: eventTags.time_of_day || eventTags.when,
    
    // Additional metadata
    achievements: eventTags.achievements || [],
    customText: eventTags.custom_text || {},
    designIntensity: eventTags.intensity || 'medium'
  };
};

export default {
  generateDynamicScene,
  processEventTags,
  LAYOUT_TEMPLATES,
  SPORT_OVERLAYS
}; 